import React, { useState } from 'react';
import { formatDateString } from '../lib/utils';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Pencil } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';

interface AgeGroup {
  id: number;
  group_name: string;
  sex: 'male' | 'female';
  birth_date_min: string;
  birth_date_max: string;
}

interface Coach {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
}

interface Assignment {
  id: number;
  age_group_id: number;
  coach_id: string;
}

interface CoachAssignmentMatrixProps {
  ageGroups: AgeGroup[];
  coaches: Coach[];
  assignments: Assignment[];
  onToggleAssignment: (ageGroupId: number, coachId: string) => void;
  onEditGroup: (group: AgeGroup) => void;
}

export function CoachAssignmentMatrix({
  ageGroups,
  coaches,
  assignments,
  onToggleAssignment,
  onEditGroup,
}: CoachAssignmentMatrixProps) {
  const [selectedTab, setSelectedTab] = useState<'male' | 'female'>('male');

  const boysGroups = ageGroups.filter((g) => g.sex === 'male').sort((a, b) => a.group_name.localeCompare(b.group_name));
  const girlsGroups = ageGroups.filter((g) => g.sex === 'female').sort((a, b) => a.group_name.localeCompare(b.group_name));

  return (
    <Tabs value={selectedTab} onValueChange={val => setSelectedTab(val as 'male' | 'female')} className="w-full">
      <TabsList className="mb-4">
        <TabsTrigger value="male">Boys</TabsTrigger>
        <TabsTrigger value="female">Girls</TabsTrigger>
      </TabsList>
      <TabsContent value="male">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 bg-white z-10 min-w-[200px]">Age Group</TableHead>
                {coaches.map((coach) => (
                  <TableHead key={coach.id} className="text-center">
                    {coach.first_name} {coach.last_name}
                  </TableHead>
                ))}
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {boysGroups.map((group) => (
                <TableRow key={group.id}>
                  <TableCell className="sticky left-0 bg-white z-10">
                    <div className="font-medium">{group.group_name} (Boys)</div>
                    <div className="text-sm text-gray-500">
                      {formatDateString(group.birth_date_min)} - {formatDateString(group.birth_date_max)}
                    </div>
                  </TableCell>
                  {coaches.map((coach) => (
                    <TableCell key={coach.id} className="text-center">
                      <Switch
                        checked={assignments.some(
                          (a) => a.age_group_id === group.id && a.coach_id === coach.id
                        )}
                        onCheckedChange={() => onToggleAssignment(group.id, coach.id)}
                      />
                    </TableCell>
                  ))}
                  <TableCell className="text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEditGroup(group)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </TabsContent>
      <TabsContent value="female">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 bg-white z-10 min-w-[200px]">Age Group</TableHead>
                {coaches.map((coach) => (
                  <TableHead key={coach.id} className="text-center">
                    {coach.first_name} {coach.last_name}
                  </TableHead>
                ))}
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {girlsGroups.map((group) => (
                <TableRow key={group.id}>
                  <TableCell className="sticky left-0 bg-white z-10">
                    <div className="font-medium">{group.group_name} (Girls)</div>
                  </TableCell>
                  {coaches.map((coach) => (
                    <TableCell key={coach.id} className="text-center">
                      <Switch
                        checked={assignments.some(
                          (a) => a.age_group_id === group.id && a.coach_id === coach.id
                        )}
                        onCheckedChange={() => onToggleAssignment(group.id, coach.id)}
                      />
                    </TableCell>
                  ))}
                  <TableCell className="text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEditGroup(group)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </TabsContent>
    </Tabs>
  );
}